import { computed, inject, signal, Service } from '@angular/core';
import { UserSettingEventConsumerService } from '../../../events/user-setting/user-setting-event-consumer.service';
import { UserSettingEventProducerService } from '../../../events/user-setting/user-setting-event-producer.service';
import { resolvePriceBasis } from '../../../models/portfolio.utils';
import {
	PortfolioPriceBasis,
	PortfolioSortCriterion,
	PortfolioSortDirection
} from '../../../models/user-setting.model';
import { MarketSessionService } from '../../../services/market-session.service';

/**
 * What the eye toggle shows in place of a figure once amounts are hidden.
 * Re-exported from `models/masked-value.model.ts`, which «عدم نمایش مانده» shares.
 */
export { MASKED_VALUE } from '../../../models/masked-value.model';

/**
 * The portfolio's view-only state, held above the screens that use it.
 *
 * Five things outlive a single component here:
 *
 * - `amountsHidden`, the eye toggle. It reaches the summary — the headline total
 *   and the three info cards — and the two figures on each asset card that say
 *   what the position is worth (ارزش فعلی, سود زیان فعلی); the detail screen
 *   masks nothing. Spanning two components is why it cannot live in the header
 *   that owns the button, and it is a stored user setting rather than a signal of
 *   its own: a privacy choice that resets itself is not much of a privacy choice,
 *   so it follows the trader across sessions and devices the way the theme does.
 * - `frozenCardWidths`, which must stay in lockstep with `amountsHidden`. Held
 *   here for that reason: the flag now outlives the summary component, so a
 *   per-component copy would come back empty on the trader's return and collapse
 *   cards the stored flag still says are frozen.
 * - `listScrollTop`, so returning from a holding's detail puts the trader back on
 *   the same row they opened, not at the top of the list.
 * - `sortCriterion` / `sortDirection`, the order the holdings are listed in.
 *   Stored like the mask, and for one more reason: the handoff asks for the last
 *   sort to survive the app closing.
 * - `priceBasis`, مبنای قیمت — which price every value and profit/loss figure in
 *   the portfolio is measured at. Stored for the same reasons, and it reaches
 *   further than the others: `PortfolioStore` reads {@link effectivePriceBasis}
 *   to revalue the holdings, so the summary, the cards and the detail screen all
 *   answer to this one setting.
 *
 * Deliberately separate from `PortfolioStore`: this is display preference rather
 * than portfolio data.
 */
@Service()
export class PortfolioViewStateService {
	private readonly userSetting = inject(UserSettingEventConsumerService);
	private readonly userSettingProducer = inject(UserSettingEventProducerService);
	private readonly marketSession = inject(MarketSessionService);

	/**
	 * Whether the portfolio's own figures are masked, as the stored setting has
	 * it. Reading from the event bus rather than a local signal means the cached
	 * copy already masks the first paint of the next session, before the API
	 * answers.
	 */
	readonly amountsHidden = computed(() => this.userSetting.getSetting().portfolio.amountsHidden);

	/**
	 * The criterion and direction the holdings are listed by, as the stored
	 * setting has them. Read from the event bus for the same reason as the mask:
	 * the cached record already carries the trader's last sort, so the first
	 * paint of the next session is in their order rather than the default one.
	 */
	readonly sortCriterion = computed(() => this.userSetting.getSetting().portfolio.sortCriterion);
	readonly sortDirection = computed(() => this.userSetting.getSetting().portfolio.sortDirection);

	/** مبنای قیمت as the trader chose it — خودکار included. What the settings sheet shows. */
	readonly priceBasis = computed(() => this.userSetting.getSetting().portfolio.priceBasis);

	/**
	 * The same setting with خودکار already resolved against the market session —
	 * what the figures are actually measured at.
	 *
	 * A computed rather than a one-off read, so the portfolio revalues itself when
	 * the session opens or closes under a trader who is still on the screen, not
	 * only when they choose a basis.
	 */
	readonly effectivePriceBasis = computed(() => resolvePriceBasis(this.priceBasis(), this.marketSession.isOpen()));

	/**
	 * Card widths captured at the moment of hiding, in card order.
	 *
	 * Per the handoff: a summary card is normally as wide as its content, but once
	 * the figures become asterisks that content is much narrower. Letting the
	 * cards shrink would pull the row out of its scrollable state, drop the
	 * indicator beneath it and shift the whole section — so the measured widths
	 * are frozen for as long as the amounts stay hidden.
	 *
	 * Empty when the screen opens already masked: there was no wider layout the
	 * trader saw, so there is nothing to hold still, and the row measures itself
	 * honestly instead.
	 */
	readonly frozenCardWidths = signal<readonly number[]>([]);

	/**
	 * Scroll offset of the holdings list, in pixels.
	 *
	 * Written as the list scrolls and read back when it is rebuilt. The store keeps
	 * the holdings themselves in memory, so the restored list is the same height it
	 * was and the offset still points at the same card.
	 */
	readonly listScrollTop = signal(0);

	/**
	 * Persist the eye toggle.
	 *
	 * Resolves `false` when the write failed, in which case the mask keeps the
	 * state the API still holds — the same bargain the theme toggle makes, so the
	 * caller can report the failure rather than drift from what is saved.
	 */
	setAmountsHidden(hidden: boolean): Promise<boolean> {
		return this.userSettingProducer.savePortfolioAmountsHidden(hidden);
	}

	/**
	 * Persist the holdings' sort. Resolves `false` when the write failed, in
	 * which case the list keeps the order the API still holds — the same bargain
	 * the mask makes above.
	 */
	setSort(criterion: PortfolioSortCriterion, direction: PortfolioSortDirection): Promise<boolean> {
		return this.userSettingProducer.savePortfolioSort(criterion, direction);
	}

	/**
	 * Persist مبنای قیمت — scenario 4. Resolves `false` when the write failed, in
	 * which case the figures keep the basis the API still holds, the same bargain
	 * the mask and the sort make above.
	 */
	setPriceBasis(priceBasis: PortfolioPriceBasis): Promise<boolean> {
		return this.userSettingProducer.savePortfolioPriceBasis(priceBasis);
	}

	setFrozenCardWidths(widths: readonly number[]): void {
		this.frozenCardWidths.set(widths);
	}

	setListScrollTop(offset: number): void {
		this.listScrollTop.set(offset);
	}
}
